import { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useStockFilter } from "hooks";
import { RootState } from "redux/store";
import { ProTable, Title, FormInput, Spinner, Modal } from "components";
import { ManagedStocksPopup } from "popups/ManagedStocks/ManagedStocks";
import { getStockGroupDetails, addSelectedRows, getStockGroupsTable, updateStockGroupField, updateStockGroup, createStockGroup, cleanStockGroupDetails, importStockToStockGroup } from "redux/slices/StockGroups";
import { getRowIds } from "helpers/data/dataHandling";
import { ManagedStockGroup } from "models/dbModels/managedStockGroups/ManagedStockGroup";

export function NewStockGroup() {
	const form = useRef(null);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { stockGroupId } = useParams();
	const getStockFilterOptions = useStockFilter();
	const [loading, setLoading] = useState<boolean>(false);
	const [isValidated, setValidated] = useState<boolean>(false);
	const stocks = useAppSelector((state: RootState) => state.stocks);
	const [isNewStockGroup, setIsNewStockGroup] = useState<boolean>(true);
	const stockGroup = useAppSelector((state: RootState) => state.stockGroups);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const user = useAppSelector((state: RootState) => state.logedinUser);

	const columnsb = [
		{
			Header: "Name",
			accessor: "name",
			width: 1100,
			Cell: (row: any) => user.permissions.managedStock.canUpdate ? <Link to={`/app/stock/${row.cell.row.original.id}`}>{row.cell.row.original.name}</Link> : <>{row.cell.row.original.name}</>,

		},
	];

	useEffect(() => {
		setValidated(false);
		handleChange("selectedRows", []);
		if (stockGroupId) {
			const id = parseInt(stockGroupId);
			dispatch(getStockGroupDetails(id));
			dispatch(getStockGroupsTable({ stockGroupIds: id ? [id] : [] }));
			setIsNewStockGroup(false);
		} else {
			dispatch(cleanStockGroupDetails());
			setIsNewStockGroup(true);
		}
	}, [stockGroupId]);

	useEffect(() => {
		if (stockGroupId){
			const id = parseInt(stockGroupId);
			dispatch(getStockGroupsTable({ stockGroupIds: id ? [id] : [] }));
		}
	}, [stockGroup.stockPopup]);

	useEffect(() => {
		handleChange("selectedRows", []);
	}, [stockGroup.pageSize]);

	function handleSubmit() {
		setShowConfirmModal(false);
		setLoading(true);
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			const payload: ManagedStockGroup = {
				id: stockGroup.id,
				name: stockGroup.name,
				locked: stockGroup.locked,
				modelId: stockGroup.modelId,
			}
			if (isNewStockGroup) {
				dispatch(createStockGroup(payload)).then((res) => {
					setLoading(false);
					handleUpdate();
				}).finally(() => {
					setLoading(false);
				});
			} else {
				dispatch(updateStockGroup(payload)).then(() => handleUpdate()).finally(() => {
					setLoading(false);
				});;
			}
		} else {
			setLoading(false);
		}
		setValidated(true);
	}

	function handleUpdate() {
		setLoading(false);
		navigate("/app/stock-groups");
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateStockGroupField({ key: option, value: optionValue }));
	}

	function handleDelete(deleteCallback: () => void) {
		let selectedRows: any[] = [];
		stockGroup.selectedRows.forEach((page: any) => {
			page.forEach((row: any) => {
				selectedRows.push(row.id);
			});
		});
		handleChange("selectedRows", []);
		const id = parseInt(stockGroupId || '');

		dispatch(importStockToStockGroup({ filter: { ids: selectedRows }, stockGroup: { isChanging: true, newValue: null, oldValue: stockGroup.id } })).then(() => {
			deleteCallback();
			dispatch(getStockGroupsTable({ stockGroupIds: id ? [id] : [] }));
		});
	}

	function handleCloseStockPopup() {
		handleChange("stockPopup", false);
	}

	function onSearchClick(keyword: string) {
		handleChange("pageIndex", 0);
		handleChange("keyword", keyword);
		dispatch(getStockGroupsTable({
			keyword: keyword,
			offset: 0,
			stockGroupIds: stockGroupId ? [parseInt(stockGroupId)] : [],
			limit: stockGroup.pageSize
		}));
	}

	function updateTableRows(dataTableState: any) {
		handleChange("pageIndex", dataTableState.pageIndex);
		handleChange("pageSize", dataTableState.pageSize);
		dispatch(getStockGroupsTable({
			keyword: stockGroup.keyword,
			offset: (dataTableState.pageIndex * dataTableState.pageSize),
			stockGroupIds: stockGroupId ? [parseInt(stockGroupId)] : [],
			limit: dataTableState.pageSize
		}));
	}

	function selectedRowsUpdate(rows: any, index: any) {
		dispatch(addSelectedRows({ rows: rows, index: index }));
	}

	function managedStocksPopupSave() {
		dispatch(importStockToStockGroup({
			filter: {
				...getStockFilterOptions(),
				ids: (stocks.selectAllFilter ? [] : getRowIds(stocks.selectedRows)),
				excludedIds: (stocks.selectAllFilter ? getRowIds(stocks.deselectedRows) : []),
			},
			stockGroup: {
				isChanging: true,
				newValue: stockGroup.id,
				oldValue: null
			}
		}));

		stocks.selectedRows.forEach((row: any, index: any) => dispatch(addSelectedRows({ rows: [], index: index })));
		setTimeout(() => {			
			dispatch(getStockGroupsTable({ stockGroupIds: stockGroupId ? [parseInt(stockGroupId)] : [] })).then(() => {
				dispatch(updateStockGroupField({ key: "stockPopup", value: false }));
			});
		}, 1000);
	}

	return (
		<>
			<Title pageTitle={isNewStockGroup ? "New Stock Group" : "Update Stock Group"} crumbs={[{ name: "Stock Groups", path: "/app/stock-groups" }]} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col xs={12}>
									<Modal
										title="Confirm"
										show={showConfirmModal}
										buttonType="primary"
										buttonText="Continue"
										buttonCloseText="Cancel"
										handleSubmit={handleSubmit}
										handleClose={() => setShowConfirmModal(false)}
										description={
											<>
												<p>You are about to sync the stock group down to all stores.</p>
												<p>To sync the stock lines within, go to Managed Stocks &gt; Sync &gt; Select Stock Group.</p>
												<p>Are you sure you want to continue?</p>
											</>
										}
									/>
									<Form onSubmit={handleSubmit} validated={isValidated} ref={form}>
										<Row>
											<h4 className="mt-2">Stock Group Details</h4>
											<Col md={6}>
												<FormInput
													required
													label="Name"
													type="text"
													name="stockgroup-name"
													placeholder="Enter stock group name"
													containerClass={"mb-3"}
													value={stockGroup.name}
													onChange={(e) => handleChange("name", e.target.value)}
												/>
											</Col>
										</Row>
										{isNewStockGroup === false && <>
											<Row>
												<Col md={12}>
													<ManagedStocksPopup show={stockGroup.stockPopup} onClose={handleCloseStockPopup} onSave={managedStocksPopupSave} />
													<ProTable
														pageSize={100}
														isSortable={true}
														pagination={true}
														isSelectable={true}
														data={stockGroup}
														isSearchable={true}
														columns={columnsb}
														loading={stockGroup.loading}
														removeRows={(deleteCallback) => handleDelete(deleteCallback)}
														table={stockGroup.stockTable}
														onSearchClick={onSearchClick}
														updateTableRows={updateTableRows}
														selectedRows={stockGroup.selectedRows}
														selectedRowsUpdate={selectedRowsUpdate}
														addNewRow={() => handleChange("stockPopup", true)}
														reloadTableRows={() => dispatch(getStockGroupsTable({ stockGroupIds: stockGroupId ? [parseInt(stockGroupId)] : [] }))}
													/>
												</Col>
											</Row>
										</>
										}
										<Row className="mt-3">
											<Col sm={6}>
												<Link to="/app/stock-groups" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
													<i className="mdi mdi-arrow-left"></i> Back to All Stock Groups {" "}
												</Link>
											</Col>
											<Col sm={6} className="d-flex flex-row-reverse">
												<Button variant="primary" className="btn cta-button" disabled={loading} onClick={() => setShowConfirmModal(true)}>
													{loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
													{loading ? <>Saving...</> : <>Save</>}
												</Button>
											</Col>
										</Row>
									</Form>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}